import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	ApLoaderDefault,
	ApLoaderText,
	colors,
} from '@alixpartners/ui-components';
import isEqual from 'lodash/isEqual';

import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import { convertToArray } from 'Details/utils';
import { InvestigateContext } from '../context';
import WaterfallChart from './WaterfallChart';
import { getMainChart } from 'utils/apiRequests';
import {
	BREAKDOWN_BY,
	CHART_TYPES,
	CHART_TYPE_MAP,
	SPEND_TYPE,
	SPEND_BY_LABELS,
	SPEND_BY_PROPS,
	SPEND_TYPE_LABELS,
	makeEmptyFromUnknown,
	makeUnknownFromEmpty,
} from '../utils';
import { createBreakdownSeries, createNoneSeries } from './utils';
import { Container, LoaderWrapper } from './WaterfallChart.styles';
import { SettingsContext } from 'contexts/SettingsContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';

export default React.memo(
	({ apiParams, showExplorePage, chartType, setChartRef }) => {
		const [mainChartData, setMainChartData] = useState({});
		const [directIndirectData, setDirectIndirectData] = useState({});
		const [sourceableData, setSourceableData] = useState({});
		const [isLoading, setIsLoading] = useState(true);
		const [topVendorsModalData, setTopVendorsModalData] = useState(null);
		const [changeStatusModalData, setChangeStatusModalData] = useState(null);
		const [moveToBinModalData, setMoveToBinModalData] = useState(null);
		const {
			options: { currency: currencyList },
		} = useContext(SettingsOptionsContext);
		const { refreshContent } = useContext(InvestigateContext);
		const queryParams = useContext(QueryStringContext);
		const { hideDecimals } = useContext(SettingsContext);
		const { settings } = useContext(SpendCubeSettingsContext);
		const { tailSpend } = settings;
		const { useNetSpend } = tailSpend;

		const {
			currency,
			showNonSourceable,
			selectedItem,
			setQueryStringStateAndPush,
			spendBy,
			spendType,
		} = queryParams;

		const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
		const {
			getTailSpendOnly,
			includeTailSpend,
			hierarchy: paramsHierarchy,
		} = apiParams;
		let hierarchy = [...paramsHierarchy];
		if (hierarchy.length === 4) {
			let requestHierarchy = [...hierarchy];
			requestHierarchy.pop();
			hierarchy = requestHierarchy;
		}
		if (spendBy !== SPEND_BY_PROPS.CATEGORY && hierarchy.length) {
			hierarchy = [];
		}

		const isBreakdownBy =
			apiParams.pivotingFilters && apiParams.pivotingFilters.length;

		const apiParamsRef = useRef(apiParams);

		if (!isEqual(apiParamsRef.current, apiParams)) {
			apiParamsRef.current = apiParams;
		}

		useEffect(
			() => {
				const requestParams = apiParamsRef.current;
				if (!requestParams) return;
				const { hierarchy } = requestParams;
				const queryParamsValues = {
					Harmonized: true,
					CalculationType: 1,
					Skip: 0,
					Take: 9,
				};
				if (selectedItem && mainChartData.body) return;

				if (!hierarchy.length && !requestParams.filters.includes('spendType')) {
					setIsLoading(true);
					setMainChartData({});
					let sourceableFilters = [...requestParams.filters];
					let sourceableFilterValues = [...requestParams.filterValues];
					if (sourceableFilters.includes('sourceable')) {
						const index = sourceableFilters.indexOf('sourceable');
						sourceableFilters.splice(index, 1);
						sourceableFilterValues.splice(index, 1);
					}

					getMainChart({
						...queryParamsValues,
						...requestParams,
						filters: sourceableFilters,
						filterValues: sourceableFilterValues,
						dimensions: 'sourceable',
						includeTailSpend: false,
						getTailSpendOnly: false,
						calculateTailSpend: false,
					}).then(res => {
						setSourceableData(res);
						if (directIndirectData) setIsLoading(false);
					});

					getMainChart({
						...queryParamsValues,
						...requestParams,
						dimensions: 'spendType',
					}).then(result => {
						setDirectIndirectData(result);
						if (sourceableData) setIsLoading(false);
					});
				} else {
					setIsLoading(true);
					const params = { ...queryParamsValues, ...requestParams };

					let validPivotFilters = true;

					if (params.splitSpendByDimension === '8') {
						const selectedFilterValues = Array.isArray(params.filterValues)
							? params.filterValues.filter(
									v => typeof v === 'string' && v.trim() !== ''
							  )
							: [];

						if (Array.isArray(params.pivotingFilters)) {
							params.pivotingFilters =
								selectedFilterValues.length > 0
									? [...selectedFilterValues]
									: [...params.pivotingFilters];
						}
						validPivotFilters =
							!selectedFilterValues.length ||
							params.pivotingFilters?.length === selectedFilterValues.length;
					}

					if (params.splitSpendByDimension !== '8' && settings?.isPortco) {
						params.pivotingFilters = Array.isArray(
							requestParams.pivotingFilters
						)
							? [...requestParams.pivotingFilters]
							: [];
					}

					const canFetch =
						params.splitSpendByDimension === BREAKDOWN_BY.NONE ||
						(params.splitSpendByDimension === '8'
							? validPivotFilters
							: params.pivotingFilters?.length > 0 || settings?.isPortco);

					if (canFetch) {
						setMainChartData({});

						if (hierarchy.length === 4) {
							params.hierarchy = [...hierarchy].slice(0, -1);
						}

						if (
							spendBy !== SPEND_BY_PROPS.CATEGORY &&
							params.hierarchy.length
						) {
							params.hierarchy = [];
							params.dimensions = [convertToArray(params.dimensions)[0]];
						}

						getMainChart(params)
							.then(result => {
								setMainChartData(result);
								setIsLoading(false);
							})
							.catch(() => setIsLoading(false));
					} else {
						setIsLoading(false);
					}
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[apiParamsRef.current, selectedItem, spendType, spendBy]
		);

		const handleViewTransactionsClick = index => {
			const options = {};

			const exploreHierarchy = [...hierarchy];
			const name = (isWaterfall ? [...categories].reverse() : categories)[
				index
			];
			let categoryName = name;
			if (
				categoryName === 'Tail spend' ||
				categoryName === 'Indirect' ||
				categoryName === 'Direct'
			) {
				categoryName = '';
			}

			if (spendType != null) {
				options.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
			}
			if (name === 'Indirect' || name === 'Direct') {
				options.spendType = name.toUpperCase();
			}

			if (spendBy === SPEND_BY_PROPS.CATEGORY) {
				exploreHierarchy.push(categoryName);
			} else {
				options.selectedDimensionKey = spendBy;
				options.selectedDimensionName = categoryName;
			}

			const isTailSpendData = getTailSpendOnly || name === 'Tail spend';

			if (isTailSpendData || includeTailSpend) {
				options.isTailSpend = isTailSpendData;
			}

			if (exploreHierarchy && exploreHierarchy.length > 0) {
				options.hierarchy = exploreHierarchy;
			}

			if (
				queryParams.indirectTaxonomy === '2' &&
				queryParams.directTaxonomy === '2'
			) {
				options.isCustom = true;
			}
			showExplorePage(options, name);
		};

		const showTopVendorsModal = index => {
			const name = (isWaterfall ? [...categories].reverse() : categories)[
				index
			];
			const data = {
				includeTailSpend: !includeTailSpend,
				name,
			};

			if (spendBy === SPEND_BY_PROPS.CATEGORY) {
				data.hierarchy = [...hierarchy, makeEmptyFromUnknown(name)];
			} else {
				data.extraFilters = {
					...data.extraFilters,
					[spendBy]: [makeEmptyFromUnknown(name)],
				};
			}

			setTopVendorsModalData(data);
		};

		const hideTopVendorsModal = () => {
			setTopVendorsModalData(null);
		};

		const handleChangeStatus = index => {
			const exploreHierarchy = [...hierarchy];
			const name = (isWaterfall ? [...categories].reverse() : categories)[
				index
			];

			const data = {
				dimensions: apiParams.dimensions,
				isPmi: true,
			};

			if (spendBy === SPEND_BY_PROPS.CATEGORY) {
				data.categoryHierarchy = [...exploreHierarchy, name];
			} else {
				data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
				data.selectedDimensionKey = spendBy;
				data.selectedDimensionKeyTitle = SPEND_BY_LABELS[spendBy];
				data.selectedDimensionName = name;
			}

			if (spendType != null) {
				data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
			}

			setChangeStatusModalData(data);
		};

		const handleMoveToBin = index => {
			const exploreHierarchy = [...hierarchy];
			const name = (isWaterfall ? [...categories].reverse() : categories)[
				index
			];

			const data = { isPmi: true };

			if (spendBy === SPEND_BY_PROPS.CATEGORY) {
				data.categoryHierarchy = [...exploreHierarchy, name];
			} else {
				data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
				data.selectedDimensionKey = spendBy;
				data.selectedDimensionKeyTitle = SPEND_BY_LABELS[spendBy];
				data.selectedDimensionName = name;
			}

			if (spendType != null) {
				data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
			}

			setMoveToBinModalData(data);
		};
		const type = chartType
			? CHART_TYPE_MAP[chartType] || chartType
			: CHART_TYPE_MAP[CHART_TYPES.BAR];

		const isWaterfall = type === CHART_TYPE_MAP[CHART_TYPES.WATERFALL];
		const data = mainChartData && mainChartData.body ? mainChartData.body : [];
		const isOthers = mainChartData && mainChartData.notLoaded;
		let categories = [];
		const isSpendTypeSelected =
			apiParams && apiParams.filters.includes('spendType');
		const isUseMainChartData = hierarchy.length || isSpendTypeSelected;
		const isSourceable = showNonSourceable === 'false';

		const isTailSpendData = isUseMainChartData
			? mainChartData && mainChartData.tailSpend
			: directIndirectData && directIndirectData.tailSpend;

		const isTailSpendColumn =
			includeTailSpend && !getTailSpendOnly && isTailSpendData;

		if (isUseMainChartData) {
			categories = data
				? (isWaterfall ? [...data].reverse() : data).map(item =>
						makeUnknownFromEmpty(item.name)
				  )
				: [];

			const spendLabel =
				Number(spendType) === SPEND_TYPE.DIRECT ? 'Direct' : 'Indirect';
			let firstItem = hierarchy.length
				? hierarchy[hierarchy.length - 1]
				: spendLabel;
			if (hierarchy.length === 4) {
				firstItem = hierarchy[hierarchy.length - 2];
			}
			if (isWaterfall) {
				categories = [...categories, '', firstItem];
				if (isOthers) {
					categories = ['Others', ...categories];
				}
				if (isTailSpendColumn) {
					categories = ['Tail spend', ...categories];
				}
			} else {
				if (isOthers) {
					categories = [...categories, 'Others'];
				}
				if (isTailSpendColumn) {
					categories = [...categories, 'Tail spend'];
				}
			}
		} else {
			categories = isSourceable
				? ['Non Sourceable', 'Sourceable', '', 'Indirect', 'Direct']
				: ['Sourceable', 'Non Sourceable', '', 'Indirect', 'Direct'];
			if (isTailSpendColumn) {
				categories = [...categories, 'Tail spend'];
			}
			if (isWaterfall) {
				categories.reverse();
			}
		}

		let series = [];

		if (isUseMainChartData && isBreakdownBy) {
			series = createBreakdownSeries(mainChartData, isWaterfall);
			if (isWaterfall) {
				series.reverse();
			}
		} else if (isUseMainChartData) {
			series = createNoneSeries(mainChartData, isWaterfall, useNetSpend);
			if (isWaterfall) {
				series.reverse();
			}
		} else if (
			sourceableData &&
			sourceableData.body &&
			sourceableData.body.length &&
			directIndirectData &&
			directIndirectData.body &&
			directIndirectData.body.length
		) {
			const getSpend = item =>
				item ? (useNetSpend ? item.spend : item.positiveSpend) : 0;
			const direct = directIndirectData.body.find(
				item => item.name === 'Direct'
			);
			const indirect = directIndirectData.body.find(
				item => item.name === 'Indirect'
			);
			const sourceable = sourceableData.body.find(item => item.name === 'True');
			const nonSourceable = sourceableData.body.find(
				item => item.name === 'False'
			);

			const sourceableSpend = getSpend(sourceable);
			const nonSourceableSpend = getSpend(nonSourceable);
			const negativeValue = isSourceable ? sourceableSpend : nonSourceableSpend;

			const sourceableSerie = {
				name: 'Sourceable',
				lineWidth: 0,
				y: sourceableSpend,
				color: isSourceable ? colors.grey400 : colors.chartBrightTeal,
			};
			const nonSourceableSerie = {
				name: 'Non Sourceable',
				lineWidth: 0,
				y: nonSourceableSpend,
				color: isSourceable ? colors.chartBrightTeal : colors.grey400,
			};
			const rootSeries = isSourceable
				? [nonSourceableSerie, sourceableSerie]
				: [sourceableSerie, nonSourceableSerie];
			const data = [
				...rootSeries,
				{
					name: '',
					y: -negativeValue,
					lineWidth: 0,
					color: undefined,
					visible: false,
				},
				{
					name: 'Indirect',
					lineWidth: 0,
					y: getSpend(indirect),
					color: colors.chartBrightTeal,
				},
				{
					name: 'Direct',
					lineWidth: 0,
					y: getSpend(direct),
					color: colors.chartBrightTeal,
				},
			];
			if (directIndirectData.tailSpend) {
				data.push({
					name: 'Tail spend',
					lineWidth: 0,
					y: useNetSpend
						? directIndirectData.tailSpend.spend
						: directIndirectData.tailSpend.positiveSpend,
					color: colors.chartBrightTeal,
				});
			}
			if (isWaterfall) {
				data.reverse();
			}
			series = [
				{
					stack: 0,
					data,
				},
			];
		} else {
			const sourceableSerie = {
				name: 'Sourceable',
				y: 0,
			};
			const nonSourceableSerie = {
				name: 'Non Sourceable',
				y: 0,
			};
			const rootSeries = isSourceable
				? [nonSourceableSerie, sourceableSerie]
				: [sourceableSerie, nonSourceableSerie];
			const data = [
				...rootSeries,
				{
					name: '',
					y: 0,
					lineWidth: 0,
					color: undefined,
					visible: false,
				},
				{
					name: 'Indirect',
					y: 0,
				},
				{
					name: 'Direct',
					y: 0,
				},
			];
			if (isWaterfall) {
				data.reverse();
			}
			series = [
				{
					stack: 0,
					data,
				},
			];
		}

		const getHiddenYAxisLabelValue = (series, isMainChart) => {
			if (isMainChart) {
				return -series.reduce((acc, serie) => {
					const dataLength = serie.data.length;
					return acc + serie.data[dataLength - 1].y;
				}, 0);
			}
			return series[0].data[series[0].data.length - 3].y;
		};
		const hideYAxisLabelValue = getHiddenYAxisLabelValue(
			series,
			isUseMainChartData
		);
		const hideIndex = isUseMainChartData ? 1 : 2;
		let selectedIndex = null;
		if (hierarchy.length === 4 || selectedItem) {
			const searchCategories = isWaterfall
				? [...categories].reverse()
				: categories;
			const searchItem = hierarchy.length === 4 ? hierarchy[3] : selectedItem;
			selectedIndex = searchCategories.indexOf(searchItem);
		}
		const onItemClick = (index, category) => {
			if (!isUseMainChartData) {
				if (category) {
					switch (category) {
						case 'Direct':
							setQueryStringStateAndPush({
								spendType: 0,
								indirectTaxonomy: 2,
								directTaxonomy: 2,
							});
							break;
						case 'Indirect':
							setQueryStringStateAndPush({ spendType: 1, indirectTaxonomy: 3 });
							break;
						case 'Sourceable':
							setQueryStringStateAndPush({
								showNonSourceable: false,
							});
							break;
						case 'Non Sourceable':
							setQueryStringStateAndPush({
								showNonSourceable: true,
							});
							break;
						default:
							break;
					}
				} else {
					// handle click by highlighted index when event
					// outside svg bar and outside HighlightBlock
					switch (index) {
						case 5:
							if (isTailSpendColumn) {
								setQueryStringStateAndPush({ getTailSpendOnly: true });
							} else {
								setQueryStringStateAndPush({ spendType: 0 });
							}

							break;
						case 4:
							if (isTailSpendColumn) {
								setQueryStringStateAndPush({ spendType: 0 });
							} else {
								setQueryStringStateAndPush({ spendType: 1 });
							}

							break;
						case 3:
							if (isTailSpendColumn) {
								setQueryStringStateAndPush({ spendType: 1 });
							}
							break;
						case 2:
							break;
						case 1:
							if (!isTailSpendColumn) {
								setQueryStringStateAndPush({
									showNonSourceable: isSourceable,
								});
							}
							break;
						case 0:
							if (isTailSpendColumn) {
								setQueryStringStateAndPush({
									showNonSourceable: isSourceable,
								});
							}
							break;
						default:
							break;
					}
				}
			} else {
				const item = isWaterfall
					? [...categories].reverse()[index]
					: categories[index];
				let hierarchyItem = makeEmptyFromUnknown(category ? category : item);

				if (hierarchyItem === 'Tail spend') {
					setQueryStringStateAndPush({
						tailSpendLevel: hierarchy.length,
					});
				} else if (hierarchyItem && hierarchyItem !== 'Others') {
					if (spendBy === SPEND_BY_PROPS.CATEGORY && hierarchy.length < 3) {
						setQueryStringStateAndPush({
							hierarchy: [...hierarchy, hierarchyItem],
						});
					} else {
						const newHierarchy = hierarchy.length === 3 ? hierarchy : [];
						setQueryStringStateAndPush({
							selectedItem: hierarchyItem,
							hierarchy: newHierarchy,
						});
					}
				}
			}
		};
		let categoryLevel = hierarchy.length;
		if (categoryLevel === 0 && spendType != null) {
			categoryLevel = 1;
		}

		return (
			<Container>
				{isLoading ? (
					<LoaderWrapper>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderWrapper>
				) : (
					<WaterfallChart
						hideDecimals={hideDecimals}
						isWaterfall={isWaterfall}
						isRoot={!hierarchy.length && !isSpendTypeSelected}
						chartType={chartType}
						categories={categories}
						series={series}
						currency={currency}
						currencySymbol={currencySymbol}
						hideYAxisLabelValue={hideYAxisLabelValue}
						hideIndex={hideIndex}
						selectedIndex={selectedIndex}
						isTailSpend={getTailSpendOnly}
						setChartRef={setChartRef}
						onViewTransactionsClick={handleViewTransactionsClick}
						onTopVendorsClick={showTopVendorsModal}
						onChangeStatusClick={handleChangeStatus}
						onMoveToBinClick={handleMoveToBin}
						onItemClick={onItemClick}
						categoryLevel={categoryLevel}
					/>
				)}
				{topVendorsModalData && (
					<TopVendorsModal
						isPmi
						onClose={hideTopVendorsModal}
						hideDecimals={hideDecimals}
						{...topVendorsModalData}
						name={topVendorsModalData.name}
					/>
				)}
				{changeStatusModalData && (
					<ChangeTypeModal
						isOpen={changeStatusModalData}
						isHarmonized
						{...changeStatusModalData}
						onClose={() => {
							setChangeStatusModalData(null);
						}}
						onSubmit={refreshContent}
					/>
				)}
				{moveToBinModalData && (
					<MoveToBinModal
						isOpen={moveToBinModalData}
						isHarmonized
						{...moveToBinModalData}
						onClose={() => {
							setMoveToBinModalData(null);
						}}
						isPmi
						onSubmit={refreshContent}
					/>
				)}
			</Container>
		);
	}
);
